import { AnyAction } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import { AddVertragToAngebote, GlobalActions } from '@ps20-frontend/shop-integration-redux';
import { App, getAnzahlVorschaeden, VwgVorbelegungdaten } from '@ps20-frontend/frontend-models';
import { TaasTarifrisikoschluesselVWGV2 } from '@ps20/bff-models';
import { VorschaedenVisibility, VwgActions } from '../vwgActions';
import { IAppState } from '../appState';

export interface Vorschaden {
  count: number;
  includeInTarifierung: boolean;
  includeInDruck: boolean;
  includeInEinreichen: boolean;
  visibleInErsatzgeschaeft: boolean;
  touched?: boolean;
  countFromRisikoortService?: number;
}

export interface Vorschaeden {
  vorhanden: boolean;
  feuerVorschaden: Vorschaden;
  leitungswasserVorschaden: Vorschaden;
  sturmhagelVorschaden: Vorschaden;
  elementarVorschaden: Vorschaden;
}

function resetVorschaeden(newState: Vorschaeden): Vorschaeden {
  newState.feuerVorschaden.count = 0;
  newState.feuerVorschaden.touched = false;
  newState.elementarVorschaden.count = 0;
  newState.elementarVorschaden.touched = false;
  newState.leitungswasserVorschaden.count = 0;
  newState.leitungswasserVorschaden.touched = false;
  newState.sturmhagelVorschaden.count = 0;
  newState.sturmhagelVorschaden.touched = false;
  return newState;
}

function resolveEffectiveVorschaden(current: Vorschaden, fallbackCount: number): Vorschaden {
  if (current.touched) {
    return current;
  }
  return { ...current, count: fallbackCount, touched: false };
}

function anyVorschadenPresent(...effectiveVorschaeden: Vorschaden[]): boolean {
  return effectiveVorschaeden.some(vorschaden => vorschaden.count > 0);
}

function resolveVorhandenKeepingUnanswered(
  currentVorhanden: boolean,
  ...effectiveVorschaeden: Vorschaden[]
): boolean {
  const present = anyVorschadenPresent(...effectiveVorschaeden);
  if (!present && currentVorhanden === null) {
    return null;
  }
  return present;
}

export const vorschaedenReducer = function (
  sectionState: Vorschaeden = null,
  action: AnyAction,
  appState: IAppState,
): Vorschaeden {
  switch (action.type) {
    case VwgActions.VORSCHAEDEN_CHANGED: {
      const newState = resetVorschaeden(cloneDeep(sectionState));
      return {
        ...newState,
        vorhanden: action.payload.vorschaeden,
      };
    }
    case VwgActions.FEUER_CHANGED: {
      return {
        ...sectionState,
        feuerVorschaden: {
          ...sectionState.feuerVorschaden,
          count: action.payload.feuer,
          touched: true,
        },
      };
    }
    case VwgActions.LEITUNGSWASSER_CHANGED: {
      return {
        ...sectionState,
        leitungswasserVorschaden: {
          ...sectionState.leitungswasserVorschaden,
          count: action.payload.leitungswasser,
          touched: true,
        },
      };
    }
    case VwgActions.STURMHAGEL_CHANGED: {
      return {
        ...sectionState,
        sturmhagelVorschaden: {
          ...sectionState.sturmhagelVorschaden,
          count: action.payload.sturmhagel,
          touched: true,
        },
      };
    }
    case VwgActions.ELEMENTAR_CHANGED: {
      return {
        ...sectionState,
        elementarVorschaden: {
          ...sectionState.elementarVorschaden,
          count: action.payload.elementar,
          touched: true,
        },
      };
    }
    case VwgActions.CONSTRUCTION_YEAR_CHANGED: {
      return sectionState;
    }
    case GlobalActions.ADD_VORVERTRAG_TO_ANGEBOTE: {
      const actionPayload: AddVertragToAngebote = action.payload;
      if (
        actionPayload.appId === App.VWG_V2 &&
        actionPayload.angebotsId === appState.angebotsId &&
        action.payload.vorvertragsNummern
      ) {
        const feuerVorschaden = resolveEffectiveVorschaden(sectionState.feuerVorschaden, 0);
        const leitungswasserVorschaden = resolveEffectiveVorschaden(
          sectionState.leitungswasserVorschaden,
          0,
        );
        const sturmhagelVorschaden = resolveEffectiveVorschaden(
          sectionState.sturmhagelVorschaden,
          0,
        );
        const elementarVorschaden = resolveEffectiveVorschaden(sectionState.elementarVorschaden, 0);
        return {
          ...sectionState,
          feuerVorschaden,
          leitungswasserVorschaden,
          sturmhagelVorschaden,
          elementarVorschaden,
          vorhanden: resolveVorhandenKeepingUnanswered(
            sectionState.vorhanden,
            feuerVorschaden,
            leitungswasserVorschaden,
            sturmhagelVorschaden,
            elementarVorschaden,
          ),
        };
      }
      return sectionState;
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const actionPayload: VwgVorbelegungdaten = action.payload;
      if (actionPayload.forApp === App.VWG_V2 && actionPayload.angebotsId === appState.angebotsId) {
        const anzVorschaedenFeuer = getAnzahlVorschaeden(
          actionPayload.vorvertragBausteinList,
          TaasTarifrisikoschluesselVWGV2.FEUER,
        );
        const anzVorschaedenLw = getAnzahlVorschaeden(
          actionPayload.vorvertragBausteinList,
          TaasTarifrisikoschluesselVWGV2.LEITUNGSWASSER,
        );
        const anzVorschaedenSt = getAnzahlVorschaeden(
          actionPayload.vorvertragBausteinList,
          TaasTarifrisikoschluesselVWGV2.STURM,
        );
        const anzVorschaedenEle = getAnzahlVorschaeden(
          actionPayload.vorvertragBausteinList,
          TaasTarifrisikoschluesselVWGV2.ELEMENTAR,
        );
        const feuerVorschaden = resolveEffectiveVorschaden(
          sectionState.feuerVorschaden,
          anzVorschaedenFeuer,
        );
        const leitungswasserVorschaden = resolveEffectiveVorschaden(
          sectionState.leitungswasserVorschaden,
          anzVorschaedenLw,
        );
        const sturmhagelVorschaden = resolveEffectiveVorschaden(
          sectionState.sturmhagelVorschaden,
          anzVorschaedenSt,
        );
        const elementarVorschaden = resolveEffectiveVorschaden(
          sectionState.elementarVorschaden,
          anzVorschaedenEle,
        );
        return {
          ...sectionState,
          vorhanden: anyVorschadenPresent(
            feuerVorschaden,
            leitungswasserVorschaden,
            sturmhagelVorschaden,
            elementarVorschaden,
          ),
          feuerVorschaden,
          leitungswasserVorschaden,
          sturmhagelVorschaden,
          elementarVorschaden,
        };
      }
      return sectionState;
    }
    case VwgActions.SHOW_VORSCHAEDEN_ERSATZGESCHAEFT: {
      const payload = action.payload as VorschaedenVisibility;
      return {
        ...sectionState,
        elementarVorschaden: {
          ...sectionState.elementarVorschaden,
          visibleInErsatzgeschaeft:
            payload.elementar ?? sectionState.elementarVorschaden.visibleInErsatzgeschaeft,
        },
        feuerVorschaden: {
          ...sectionState.feuerVorschaden,
          visibleInErsatzgeschaeft:
            payload.feuer ?? sectionState.feuerVorschaden.visibleInErsatzgeschaeft,
        },
        sturmhagelVorschaden: {
          ...sectionState.sturmhagelVorschaden,
          visibleInErsatzgeschaeft:
            payload.sturmhagel ?? sectionState.sturmhagelVorschaden.visibleInErsatzgeschaeft,
        },
        leitungswasserVorschaden: {
          ...sectionState.leitungswasserVorschaden,
          visibleInErsatzgeschaeft:
            payload.leitungswasser ??
            sectionState.leitungswasserVorschaden.visibleInErsatzgeschaeft,
        },
      };
    }
    case VwgActions.UPDATE_ELEMENTAR_VORSCHADEN_RISIKOORT: {
      return {
        ...sectionState,
        elementarVorschaden: {
          ...sectionState.elementarVorschaden,
          countFromRisikoortService: action.payload,
        },
      };
    }
    default:
      return sectionState;
  }
};
